import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { WebsiteAuditService } from '../website-audit.service';
import { AddCompetitorComponent } from '../modals/add-competitor/add-competitor.component';

@Component({
  selector: 'app-audit-subbar',
  templateUrl: './audit-subbar.component.html',
  styleUrls: ['../audit-theme.css', './audit-subbar.component.css'],
})
export class AuditSubbarComponent implements OnInit, OnDestroy {
  private subs = new Subscription();

  public selectedTask: any;
  public competitors: any[] = [];
  public activeCompetitor: any = null;
  public periodMain = '';
  public periodCompare = '';

  // Device profile the report is read for; the service holds the value so every
  // request picks it up and all sub-bar instances agree. `deviceStates` layers
  // the audit's `deviceVariants` over this list — which profiles were actually
  // run, and which are still offerable.
  private readonly devices = [
    { key: 'desktop', label: 'Desktop', icon: 'desktop_windows' },
    // { key: 'tablet', label: 'Tablet', icon: 'tablet_mac' },
    { key: 'mobile', label: 'Mobile', icon: 'smartphone' },
  ];
  public deviceStates: any[] = this.devices.map((d) => ({
    ...d,
    available: true,
    running: false,
  }));
  public activeDevice = 'desktop';
  // Shared read-only view — hide mutating actions like "Add competitor".
  public isShare = window.location.href.includes('/share/website-audit');

  // Set by the hosting tab when its data actually varies by device (UX and CR).
  // Declared by the host rather than read from the active tab index: the
  // sidenav service's getActiveTabIndex() re-emits the stored index as a side
  // effect, which would fight the tab the user just picked.
  @Input() deviceAware = false;

  constructor(
    private websiteAuditService: WebsiteAuditService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        // Only show competitors that are actively being audited AND finished
        // their first crawl (isUsable) as selectable chips. Disabled ones stay
        // available in the "Add competitor" modal; still-loading ones (isUsable
        // false) appear there as "Loading" until their data is ready.
        this.competitors = (task?.availableCompetitors || []).filter(
          (c: any) => c?.isActive !== false && c?.isUsable !== false,
        );
        this.activeCompetitor = task?.selectedCompetitor || null;
        this.buildPeriod(task);
        this.buildDeviceStates(task);
        // The payload states which device it was read for, so trust it over the
        // locally requested one — the toggle then always reflects what's shown
        // (e.g. an audit whose primary profile isn't the one we asked for).
        if (task?.device) {
          this.websiteAuditService.setSelectedDevice(task.device);
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService
        .getSelectedDevice()
        .subscribe((device: string) => {
          this.activeDevice = device;
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  private buildPeriod(task: any) {
    const dates: string[] = task?.overview?.scoreHistory?.dates || [];
    if (dates.length) {
      this.periodMain = this.fmtDate(dates[dates.length - 1]);
      this.periodCompare =
        dates.length > 1 ? `vs ${this.fmtDate(dates[dates.length - 2])}` : '';
    } else {
      const d = task?.updatedAt || task?.createdAt;
      this.periodMain = d ? this.fmtDate(d) : '';
      this.periodCompare = '';
    }
  }

  // ─── Competitor selection (broadcast so every tab re-renders) ───────────────

  isActiveCompetitor(comp: any): boolean {
    if (!this.activeCompetitor || !comp) return false;
    if (this.activeCompetitor.competitorId && comp.competitorId) {
      return this.activeCompetitor.competitorId === comp.competitorId;
    }
    return this.activeCompetitor.url === comp.url;
  }

  selectCompetitor(comp: any) {
    if (!comp || this.isActiveCompetitor(comp)) return;
    const url = comp?.url;
    if (!url) return;
    this.reloadTask(url);
  }

  // ─── Device selection ──────────────────────────────────────────────────────

  // Which profiles this audit was actually run for. Older payloads carry no
  // deviceVariants at all — treat every device as available there rather than
  // locking the toggle down on data we don't have.
  private buildDeviceStates(task: any) {
    const variants = Array.isArray(task?.deviceVariants)
      ? task.deviceVariants
      : null;

    this.deviceStates = this.devices.map((d) => {
      const variant = variants?.find(
        (v: any) => (v?.device || '').toLowerCase() === d.key,
      );
      const available = !variants || !!variant;
      const progress = variant?.progress ?? 100;
      const running = !!variant && progress < 100;

      return {
        ...d,
        available,
        running,
        isPrimary: !!variant?.isPrimary,
        tooltip: !available
          ? `No ${d.label.toLowerCase()} audit yet — click to add it`
          : running
            ? `${d.label} audit in progress (${progress}%)`
            : '',
      };
    });
  }

  // The device lives on the service, so the re-fetch below (and every later
  // request from any tab) picks it up from there — nothing to pass along.
  selectDevice(device: any) {
    if (!device) return;
    // Never run for this profile — offer to add it instead of switching to
    // data that doesn't exist.
    if (!device.available) {
      this.promptAddDevice(device);
      return;
    }
    if (device.key === this.activeDevice) return;
    this.websiteAuditService.setSelectedDevice(device.key);
    this.reloadTask(this.activeCompetitor?.url);
  }

  private promptAddDevice(device: any) {
    // The shared view is read-only, so it can only report the gap.
    if (this.isShare) {
      this.notificationService.info(
        `This audit hasn't been run for ${device.label.toLowerCase()} yet.`,
      );
      return;
    }

    const ref = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: `Add ${device.label} audit`,
        content:
          `This audit hasn't been run for ${device.label.toLowerCase()} yet. ` +
          `Add it now? The results show up here once the run finishes.`,
        confirm_btn: 'Add audit',
      },
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: any) => {
        if (result != 1) return;

        // Device variants hang off the canonical (parent) audit, not the run.
        const taskId = this.resolveParentTaskId();
        if (!taskId) return;

        this.subs.add(
          this.websiteAuditService
            .addDeviceVariant(taskId, device.key)
            .subscribe({
              next: () => {
                this.notificationService.info(
                  `${device.label} audit queued — it'll appear here once it finishes.`,
                );
                // Pick up the new variant (as "running") without a page reload.
                this.reloadTask(this.activeCompetitor?.url);
              },
              error: (err: any) => {
                this.notificationService.error(err?.error?.message);
              },
            }),
        );
      }),
    );
  }

  // Re-read the audit and broadcast it, so every tab re-renders. Used by both
  // the competitor chips and the device toggle.
  private reloadTask(competitorUrl?: string) {
    // Shared links are unauthenticated, so the authed task endpoint won't work —
    // fetch the scoped task through the public token endpoint instead.
    const request$ = this.isShare
      ? this.getShareToken()
        ? this.websiteAuditService.getAuditTaskFromToken(
            this.getShareToken(),
            competitorUrl,
          )
        : null
      : this.selectedTask?.taskId
        ? this.websiteAuditService.getAuditTask(
            this.selectedTask.taskId,
            competitorUrl,
          )
        : null;

    if (!request$) return;

    // Show the switching overlay across every tab until the new data lands.
    this.websiteAuditService.setCompetitorSwitching(true);

    this.subs.add(
      request$.subscribe({
        next: (res: any) => {
          // Same audit, so this won't retrigger a full task reload — it just
          // swaps the scoped overview/sections across all tabs.
          this.websiteAuditService.updateSelectedAuditTask({
            ...this.selectedTask,
            ...res,
          });
          this.websiteAuditService.setCompetitorSwitching(false);
        },
        error: () => {
          this.websiteAuditService.setCompetitorSwitching(false);
        },
      }),
    );
  }

  private getShareToken(): string {
    return (
      sessionStorage.getItem('shareableToken') ||
      sessionStorage.getItem('shareable-website-audit-auth') ||
      ''
    );
  }

  addCompetitor() {
    const parentTaskId = this.resolveParentTaskId();
    if (!parentTaskId) return;

    const competitors = this.selectedTask?.availableCompetitors || [];
    const ref = this.dialog.open(AddCompetitorComponent, {
      width: '430px',
      data: {
        taskId: parentTaskId,
        competitors,
        // Source list already is the benchmark set → all shown active.
        activeCompetitorIds: competitors
          .map((c: any) => c?.competitorId)
          .filter(Boolean),
      },
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: any) => {
        if (!result) return;
        const taskId = this.selectedTask?.taskId;
        if (!taskId) return;
        this.websiteAuditService.getAuditTask(taskId).subscribe({
          next: (res: any) => {
            this.websiteAuditService.updateSelectedAuditTask({
              ...this.selectedTask,
              ...res,
            });
          },
        });
      }),
    );
  }

  // Competitors live on the biweekly parent task, not the run.
  private resolveParentTaskId(): string | null {
    const t = this.selectedTask;
    if (!t) return null;
    if (t.parentTaskId) return t.parentTaskId;
    const hist = Array.isArray(t.taskHistory) ? t.taskHistory : [];
    const root = hist.find(
      (h: any) => h?.taskId && h.taskId === h?.parentTaskId,
    );
    return root?.taskId || t.taskId || null;
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  getCompChipLabel(comp: any): string {
    return comp?.domain || this.getDomain(comp?.url || '');
  }

  getAuditFrequencyLabel(): string {
    const freq = this.selectedTask?.auditFrequency;
    if (!freq) return 'ONE-TIME';
    return freq === 'biweekly' ? 'BIWEEKLY' : freq.toUpperCase();
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  private fmtDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }

  public get showDeviceToggle(): boolean {
    const isMetrobutiqueSelected =
      this.selectedTask?.domain?.includes('metroboutique') ||
      this.selectedTask?.target?.includes('metroboutique');

    // Only the device-aware tabs (UX, CR) read per-device data, so the toggle
    // would be misleading on the rest.
    return !!this.selectedTask && isMetrobutiqueSelected && this.deviceAware;
  }
}
